import { useState } from 'react'
import { KanbanCard } from './KanbanCard'
import type { FeedbackItem } from '../../lib/feedbackApi'

interface KanbanColumnProps {
  title: string
  status: string
  items: FeedbackItem[]
  isAdmin: boolean
  onDrop: (itemId: number, newStatus: string) => void
  onReorder: (status: string, orderedIds: number[]) => void
  onCardClick: (id: number) => void
}

export function KanbanColumn({ title, status, items, isAdmin, onDrop, onReorder, onCardClick }: KanbanColumnProps) {
  const [dragOver, setDragOver] = useState(false)
  const [dropTargetIdx, setDropTargetIdx] = useState<number | null>(null)

  function handleDragOver(e: React.DragEvent) {
    if (!isAdmin) return
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'
    if (!dragOver) setDragOver(true)
  }

  function handleDragLeave(e: React.DragEvent) {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
    const { clientX, clientY } = e
    if (
      clientX < rect.left || clientX > rect.right ||
      clientY < rect.top || clientY > rect.bottom
    ) {
      setDragOver(false)
      setDropTargetIdx(null)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    e.stopPropagation()
    const targetIdx = dropTargetIdx // capture before clearing
    setDragOver(false)
    setDropTargetIdx(null)
    const itemId = parseInt(e.dataTransfer.getData('text/plain'), 10)
    if (isNaN(itemId)) return

    // Check if the dragged item is already in this column
    const existingIdx = items.findIndex((i) => i.id === itemId)
    if (existingIdx >= 0 && targetIdx !== null && targetIdx !== existingIdx) {
      // Reorder within column
      const newOrder = items.map((i) => i.id).filter((id) => id !== itemId)
      const insertAt = targetIdx > existingIdx ? targetIdx - 1 : targetIdx
      newOrder.splice(Math.max(0, insertAt), 0, itemId)
      onReorder(status, newOrder)
    } else if (existingIdx < 0) {
      // Moving from another column
      onDrop(itemId, status)
    }
  }

  function handleCardDragOver(e: React.DragEvent, idx: number) {
    e.preventDefault()
    e.stopPropagation()
    setDropTargetIdx(idx)
  }

  return (
    <div
      className={`flex flex-col flex-1 min-w-[280px] rounded-lg transition-colors ${
        dragOver ? 'bg-primary/5 ring-2 ring-primary/20' : 'bg-secondary/30'
      }`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Column header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <span className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded-full">
          {items.length}
        </span>
      </div>

      {/* Cards area */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 max-h-[calc(100vh-280px)]">
        {items.length === 0 ? (
          <div className="flex items-center justify-center py-8 border-2 border-dashed border-border/50 rounded-lg">
            <p className="text-xs text-muted-foreground">No items</p>
          </div>
        ) : (
          <>
            {items.map((item, idx) => (
              <div
                key={item.id}
                onDragOver={(e) => handleCardDragOver(e, idx)}
                className={dropTargetIdx === idx ? 'border-t-2 border-t-primary pt-1 -mt-1' : ''}
              >
                <KanbanCard
                  item={item}
                  isAdmin={isAdmin}
                  onCardClick={onCardClick}
                />
              </div>
            ))}
            {/* Drop target after last card */}
            <div
              onDragOver={(e) => handleCardDragOver(e, items.length)}
              className={`min-h-[20px] ${dropTargetIdx === items.length ? 'border-t-2 border-t-primary pt-1' : ''}`}
            />
          </>
        )}
      </div>
    </div>
  )
}
